import { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PageHeader from "../../../Component/PageHeader";
import StatStrip, { StatItem } from "../../../Component/StatStrip";
import { PillTone } from "../../../Component/Pill";
import Loader from "../../../Component/Loader/Loader";

// Tabs of the Affiliates & payouts section; each one keeps its own route
export const AFFILIATE_TABS: [string, string, string, string][] = [
  ["/affiliate-program/account", "ri-share-forward-line", "Affiliates", "Referral partners — their clients, earnings and bank details."],
  ["/affiliate-program/commission", "ri-exchange-dollar-line", "Commission ledger", "Every commission earned when a referred client pays. It stays pending until its payout is marked paid."],
  ["/affiliate-program/commission/messaging", "ri-message-3-line", "Messaging commission", "Monthly commission on our messaging profit from credit-line clients. Review a month, then approve."],
  ["/affiliate-program/payout", "ri-bank-line", "Payouts", "Settlements and manual payouts. Transfer to the affiliate's bank, then mark the payout paid."],
  ["/affiliate-program/commission/commission-tier", "ri-percent-line", "Commission tiers", "Commission % per tier. The default tier is given to every new affiliate."],
  ["/affiliate-program/tickets", "ri-customer-service-2-line", "Tickets", "Support tickets raised by affiliates. Every reply is emailed to the affiliate."],
];

// Affiliate tickets (at_status) — same codes as the API & affiliate panel
export const TICKET_STATUS: Record<number, [PillTone, string]> = {
  0: ["warn", "Open"],
  1: ["ok", "Answered"],
  2: ["none", "Closed"],
};

// Commissions (ac_status) and payouts (ap_status) use the same codes — same as the affiliate panel
export const SETTLE_STATUS: Record<number, [PillTone, string]> = {
  0: ["warn", "Pending"],
  1: ["ok", "Paid"],
  2: ["bad", "Failed"],
};

// How a payout was paid (ap_payment_mode) — [value, label, icon]
export const PAYMENT_MODES: [string, string, string][] = [
  ["bank_transfer", "Bank transfer (NEFT / IMPS / RTGS)", "ri-bank-line"],
  ["upi", "UPI", "ri-qr-code-line"],
  ["cash", "Cash", "ri-money-rupee-circle-line"],
  ["cheque", "Cheque", "ri-file-paper-2-line"],
  ["other", "Other", "ri-more-line"],
];

export const paymentModeLabel = (mode?: string) =>
  mode ? (PAYMENT_MODES.find(m => m[0] === mode)?.[1].split(" (")[0] || mode) : "";

export const inr = (n: number | string, digits = 2) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: digits, maximumFractionDigits: digits });

export const affiliateName = (a: any) =>
  a ? (`${a.affiliate_first_name || ""} ${a.affiliate_last_name || ""}`.trim() || a.affiliate_email || "—") : "—";

export const clientName = (u: any) =>
  u ? (u.user_companyName || `${u.user_first_name || ""} ${u.user_last_name || ""}`.trim() || u.user_email || "—") : "—";

// "20%" or "20% + 5% msg" (plan commission % + messaging commission %)
export const tierRates = (tier: any) =>
  tier ? `${tier.commission_amount || 0}%${Number(tier.commission_messaging_amount) > 0 ? ` + ${tier.commission_messaging_amount}% msg` : ""}` : "";

export const nameInitials = (name: string) =>
  (name || "").split(/\s+/).filter(Boolean).map(w => w[0]).slice(0, 2).join("").toUpperCase() || "A";

interface AffiliateLayoutProps {
  actions?: ReactNode;
  stats?: StatItem[];
  loading?: boolean;
  children: ReactNode;
}

// Header + stats + tabs shared by every Affiliates & payouts tab
const AffiliateLayout = ({ actions, stats, loading, children }: AffiliateLayoutProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const tab = AFFILIATE_TABS.find(t => t[0] === pathname) || AFFILIATE_TABS[0];

  return (
    <div className="container-fluid">
      {loading && <Loader />}
      <div className="cx">
        <PageHeader crumb="Affiliates" title="Affiliates & payouts" sub={tab[3]} actions={actions} />

        {stats && <StatStrip items={stats} />}

        <div className="cx-tabs">
          {AFFILIATE_TABS.map(([path, icon, label]) => (
            <button type="button" key={path} className={path === tab[0] ? "on" : ""} onClick={() => navigate(path)}>
              <i className={icon}></i>{label}
            </button>
          ))}
        </div>

        {children}
      </div>
    </div>
  );
};

export default AffiliateLayout;
